import { t as uiText } from '../../i18n';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getBoardsByHashtag } from '../hashtag/hashtagUtils';
import { BoardWithHashtags } from '../../types/hashtag';
import { PreviewData, SearchData } from '../collaboration/searchDataUtils';
import { prepareTextForSearch } from '../collaboration/normalizationUtils';



interface BoardData {
  id: string;
  title: string;
  content: string;
  firstImageUrl?: string | null;
  lastModified: any;
  lastModifiedBy: string;
  version: number;
  slug: string;
  archived?: boolean;
  pinned?: boolean;
  // 新しいDB構造フィールド
  previewData?: PreviewData;
  searchData?: SearchData;
  isCrawled?: boolean;
  createdAt?: any;
}

// previewDataからテキストコンテンツを抽出する関数（新しいDB構造対応）
const getContentFromBoardData = (boardData: { previewData?: PreviewData, searchData?: SearchData }): string => {
  // previewData.contentがある場合は、そこからテキストを抽出（プレビュー表示用）
  if (boardData.previewData && boardData.previewData.content && boardData.previewData.content.length > 0) {
    const extractedText = extractTextFromPreviewContent(boardData.previewData.content);
    return extractedText;
  }

  // previewData.contentがない場合のみ、searchDataを使用
  if (boardData.searchData && boardData.searchData.originalContent) {
    return boardData.searchData.originalContent;
  }

  return '';
};

// previewData.content配列からテキストを抽出する関数
const extractTextFromPreviewContent = (contentArray: any[]): string => {
  if (!Array.isArray(contentArray)) return '';
  
  try {
    const textParts: string[] = [];

    contentArray.forEach((item, index) => {
      if (item && typeof item === 'object') {
        // fileAttachmentタイプの場合はファイル名を表示
        if (item.type === 'fileAttachment') {
          if (item.attrs && item.attrs.title) {
            textParts.push(`📎 ${item.attrs.title}`);
          } else {
            textParts.push(uiText("📎 Attachments"));
          }
        }
        // paragraphタイプの場合は改行を追加
        else if (item.type === 'paragraph') {
          if (item.content && Array.isArray(item.content)) {
            const paragraphText = item.content
              .map((contentItem: any) => {
                if (contentItem && typeof contentItem === 'object') {
                  if (contentItem.type === 'text' && contentItem.text) {
                    return contentItem.text;
                  }
                  if (contentItem.type === 'autoLink' && contentItem.attrs?.linkText) {
                    return contentItem.attrs.linkText;
                  }
                  if (contentItem.type === 'hardBreak') {
                    return '\n';
                  }
                }
                return '';
              })
              .join('');

            // 段落の内容を追加（改行は後で処理）
            if (paragraphText.trim()) {
              textParts.push(paragraphText);
            }
          }
          
          // paragraphタイプの場合は必ず改行を追加（空の段落も含む）
          // ただし、最後の段落の場合は改行を追加しない
          if (index < contentArray.length - 1) {
            textParts.push('\n');
          }
        }
        // 表はセル間に区切りを入れないと値の対応が読めなくなる
        else if (item.type === 'tableRow') {
          const cellTexts = (Array.isArray(item.content) ? item.content : [])
            .map((cell: any) => extractTextFromPreviewContent(Array.isArray(cell?.content) ? cell.content : []))
            .filter((text: string) => text.trim());
          if (cellTexts.length > 0) {
            textParts.push(cellTexts.join(' / '));
            textParts.push('\n');
          }
        }
        // その他のタイプも処理
        else if (item.content && Array.isArray(item.content)) {
          const itemText = extractTextFromPreviewContent(item.content);
          if (itemText) {
            textParts.push(itemText);
          }
        }
      }
    });

    const result = textParts.join('').trim();

    return result;
  } catch (error) {
    console.error('previewData.contentからのテキスト抽出エラー:', error);
    return '';
  }
};



// previewDataからサムネイル画像を取得する関数（新しいDB構造対応）
const getThumbnailFromPreviewData = (previewData: PreviewData): string | null => {
  if (!previewData) return null;
  
  // previewDataのthumbnailUrlを直接使用（既に抽出済み）
  return previewData.thumbnailUrl || null;
};



// Firestoreドキュメントデータを BoardData に変換する関数（新しいDB構造対応）
const convertDocToBoardData = (doc: any): BoardData | null => {
  const data = doc.data();

  // 新しいDB構造のpreviewDataがある場合は優先的に使用
  if (data.previewData) {
    // シンプルアクセス：直接フィールドを使用
    const boardData = { previewData: data.previewData, searchData: data.searchData };
    const content = getContentFromBoardData(boardData);
    const thumbnailUrl = getThumbnailFromPreviewData(data.previewData);

    return {
      id: doc.id,
      title: data.previewData.title || data.title || '',
      content: content,
      firstImageUrl: thumbnailUrl,
      lastModified: data.lastModified,
      lastModifiedBy: data.lastModifiedBy || '',
      version: data.version || 1,
      slug: data.slug || '',
      archived: data.archived || false,
      pinned: data.pinned || false,
      previewData: data.previewData,
      searchData: data.searchData,
      isCrawled: data.isCrawled || false,
      createdAt: data.createdAt,
    };
  }

  // previewDataがない場合はnullを返す
  return null;
};

// 全ボードから検索する関数
export const searchAllBoards = async (
  workspaceId: string, 
  searchQuery: string
): Promise<BoardData[]> => {
  if (!searchQuery.trim()) {
    return await getAllBoards(workspaceId);
  }

  try {
    const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);
    const q = query(boardsRef, orderBy('lastModified', 'desc'));
    const querySnapshot = await getDocs(q);

    const boardsData: BoardData[] = [];
    const lowerSearchQuery = searchQuery.toLowerCase();
    
    // 検索クエリを正規化
    let normalizedSearchQuery = '';
    try {
      const searchResult = prepareTextForSearch(searchQuery);
      normalizedSearchQuery = searchResult.normalized;
    } catch (error) {
      console.warn('⚠️ 検索クエリの正規化エラー:', error);
      normalizedSearchQuery = searchQuery.toLowerCase();
    }

    querySnapshot.forEach((doc) => {
      const boardData = convertDocToBoardData(doc);
      
      // previewDataがない場合はスキップ
      if (!boardData) {
        return;
      }
      
      // アーカイブされたボードは除外
      if (boardData.archived) {
        return;
      }
      
      // searchDataを使用した検索（originalとnormalized両方をチェック）
      let titleMatch = false;
      let contentMatch = false;
      
      if (boardData.searchData && 
          boardData.searchData.originalTitle && 
          boardData.searchData.normalizedTitle && 
          boardData.searchData.originalContent && 
          boardData.searchData.normalizedContent) {
        titleMatch = 
          boardData.searchData.originalTitle.toLowerCase().includes(lowerSearchQuery) ||
          boardData.searchData.normalizedTitle.includes(normalizedSearchQuery);
          
        contentMatch = 
          boardData.searchData.originalContent.toLowerCase().includes(lowerSearchQuery) ||
          boardData.searchData.normalizedContent.includes(normalizedSearchQuery);
      } else {
        // searchDataがないまたは不完全な場合はフォールバック（previewDataから検索）
        titleMatch = (boardData.title || '').toLowerCase().includes(lowerSearchQuery);
        
        // previewData.contentがある場合は、そこからも検索
        let fallbackContent = boardData.content || '';
        if (boardData.previewData && boardData.previewData.content) {
          const previewText = extractTextFromPreviewContent(boardData.previewData.content);
          if (previewText) {
            fallbackContent = previewText;
          }
        }
        contentMatch = fallbackContent.toLowerCase().includes(lowerSearchQuery);
      }
      
      if (titleMatch || contentMatch) {
        boardsData.push(boardData);
      }
    });

    return boardsData;
  } catch (error) {
    console.error('❌ searchAllBoardsエラー:', error);
    throw error;
  }
};

// 特定のハッシュタグのボードから検索する関数
export const searchBoardsByHashtag = async (
  workspaceId: string, 
  hashtagName: string, 
  searchQuery: string
): Promise<BoardData[]> => {
  try {
    // まず、指定されたハッシュタグのボードを取得
    const boardsWithHashtag = await getBoardsByHashtag(workspaceId, hashtagName);

    // ハッシュタグで取得したボードをBoardData形式に変換
    const convertedBoards: BoardData[] = boardsWithHashtag.map(board => {
      // previewDataがある場合は優先的に使用
      if (board.previewData) {
        // シンプルアクセス：直接フィールドを使用
        const boardData = { previewData: board.previewData, searchData: (board as any).searchData };
        const content = getContentFromBoardData(boardData);
        const thumbnailUrl = getThumbnailFromPreviewData(board.previewData);
        
        return {
          id: board.id,
          title: board.previewData.title || board.title,
          content: content,
          firstImageUrl: thumbnailUrl,
          lastModified: board.lastModified,
          lastModifiedBy: board.lastModifiedBy,
          version: board.version || 1,
          slug: board.slug,
          pinned: board.pinned || false,
          archived: board.archived || false,
          previewData: board.previewData,
          searchData: (board as any).searchData
        };
      }
      
      // previewDataがない場合は空のデータを返す
      return {
        id: board.id,
        title: board.title,
        content: '',
        firstImageUrl: null,
        lastModified: board.lastModified,
        lastModifiedBy: board.lastModifiedBy,
        version: board.version || 1,
        slug: board.slug,
        pinned: board.pinned || false,
        archived: board.archived || false,
        previewData: board.previewData,
        searchData: (board as any).searchData
      };
    });

    if (!searchQuery.trim()) {
      return convertedBoards;
    }

    const lowerSearchQuery = searchQuery.toLowerCase();
    
    // 検索クエリを正規化
    let normalizedSearchQuery = '';
    try {
      const searchResult = prepareTextForSearch(searchQuery);
      normalizedSearchQuery = searchResult.normalized;
    } catch (error) {
      console.warn('⚠️ 検索クエリの正規化エラー:', error);
      normalizedSearchQuery = searchQuery.toLowerCase();
    }

    // 検索クエリでフィルタリング（searchDataを使用）
    const filteredBoards = convertedBoards.filter(board => {
      let titleMatch = false;
      let contentMatch = false;
      
      if (board.searchData && 
          board.searchData.originalTitle && 
          board.searchData.normalizedTitle && 
          board.searchData.originalContent && 
          board.searchData.normalizedContent) {
        titleMatch = 
          board.searchData.originalTitle.toLowerCase().includes(lowerSearchQuery) ||
          board.searchData.normalizedTitle.includes(normalizedSearchQuery);
          
        contentMatch = 
          board.searchData.originalContent.toLowerCase().includes(lowerSearchQuery) ||
          board.searchData.normalizedContent.includes(normalizedSearchQuery);
      } else {
        // searchDataがないまたは不完全な場合はフォールバック
        titleMatch = (board.title || '').toLowerCase().includes(lowerSearchQuery);
        
        // previewData.contentがある場合は、そこからも検索
        let fallbackContent = board.content || '';
        if (board.previewData && board.previewData.content) {
          const previewText = extractTextFromPreviewContent(board.previewData.content);
          if (previewText) {
            fallbackContent = previewText;
          }
        }
        contentMatch = fallbackContent.toLowerCase().includes(lowerSearchQuery);
      }
      
      return titleMatch || contentMatch;
    });

    return filteredBoards;
  } catch (error) {
    console.error('❌ searchBoardsByHashtagエラー:', error);
    throw error;
  }
};

// 全ボードを取得する関数（検索なし）
export const getAllBoards = async (workspaceId: string): Promise<BoardData[]> => {
  try {
    const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);
    const q = query(boardsRef, orderBy('lastModified', 'desc'));
    const querySnapshot = await getDocs(q);

    const boardsData: BoardData[] = [];
    querySnapshot.forEach((doc) => {
      const boardData = convertDocToBoardData(doc);
      
      // previewDataがない場合はスキップ
      if (!boardData) {
        return;
      }
      
      // アーカイブされたボードは除外
      if (!boardData.archived) {
        boardsData.push(boardData);
      }
    });

    return boardsData;
  } catch (error) {
    console.error('❌ getAllBoardsエラー:', error);
    throw error;
  }
}; 

// アーカイブされたボードのみを取得する関数
export const getArchivedBoards = async (workspaceId: string): Promise<BoardData[]> => {
  try {
    const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);
    const q = query(boardsRef, orderBy('lastModified', 'desc'));
    const querySnapshot = await getDocs(q);

    const boardsData: BoardData[] = [];
    querySnapshot.forEach((doc) => {
      const boardData = convertDocToBoardData(doc);

      // previewDataがない場合はスキップ
      if (!boardData) {
        return;
      }

      // archivedフィールドがtrueの場合のみ追加
      if (boardData.archived === true) {
        boardsData.push(boardData);
      }
    });

    return boardsData;
  } catch (error) {
    console.error('❌ getArchivedBoardsエラー:', error);
    throw error;
  }
}; 